import type { FC, FormEvent } from 'react'

import { useState } from 'react'
import { CheckCircle2, Loader2, Send } from 'lucide-react'

import { cn } from '@/lib/utils'
import { useLocale, useT } from '@/i18n'

interface CourseRegistrationFormProps {
    /** `value` is stored (Albanian), `label` is shown in the visitor's language. */
    courses: { value: string; label: string }[]
}

type Status = 'idle' | 'submitting' | 'success' | 'error'

const fieldClass =
    'w-full rounded-xl border border-border bg-background px-4 py-3 text-sm text-foreground transition-colors placeholder:text-muted-foreground/70 focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/30'

const labelClass = 'mb-2 block text-sm font-medium text-foreground'

export const CourseRegistrationForm: FC<CourseRegistrationFormProps> = ({ courses }) => {
    const messages = useT()
    const t = messages.courseForm
    const locale = useLocale()
    const initialValues = {
        name: '',
        phone: '',
        email: '',
        course: courses[0]?.value ?? '',
        message: ''
    }

    const [values, setValues] = useState(initialValues)
    const [status, setStatus] = useState<Status>('idle')
    const [error, setError] = useState('')

    const update = (key: keyof typeof initialValues, value: string) =>
        setValues((prev) => ({ ...prev, [key]: value }))

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setError('')

        if (values.name.trim().length < 2) {
            setError(t.errors.name)
            return
        }
        if (values.phone.trim().length < 5) {
            setError(t.errors.phone)
            return
        }
        if (!/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(values.email.trim())) {
            setError(t.errors.email)
            return
        }
        if (!values.course) {
            setError(t.errors.course)
            return
        }

        setStatus('submitting')

        try {
            const response = await fetch('/api/course-registration', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...values, language: locale })
            })

            if (!response.ok) {
                const data = (await response.json().catch(() => null)) as { field?: string } | null
                if (data?.field === 'email') {
                    setStatus('idle')
                    setError(t.errors.email)
                    return
                }
                throw new Error('request failed')
            }

            setStatus('success')
            setValues(initialValues)
        } catch {
            setStatus('error')
            setError(t.errors.generic)
        }
    }

    if (status === 'success') {
        return (
            <div className='flex flex-col items-center justify-center rounded-2xl border border-border bg-card p-10 text-center'>
                <span className='grid size-16 place-items-center rounded-full bg-gold/15 text-gold-deep'>
                    <CheckCircle2 className='size-8' />
                </span>
                <h3 className='mt-6 font-display text-2xl font-semibold text-foreground'>
                    {t.successTitle}
                </h3>
                <p className='mt-3 max-w-sm text-muted-foreground'>{t.successText}</p>
                <button
                    type='button'
                    onClick={() => setStatus('idle')}
                    className='mt-6 text-sm font-semibold text-gold-deep transition-colors hover:text-navy'
                >
                    {t.again}
                </button>
            </div>
        )
    }

    return (
        <form
            onSubmit={handleSubmit}
            noValidate
            className='rounded-2xl border border-border bg-card p-6 sm:p-8'
        >
            <div className='grid gap-5 sm:grid-cols-2'>
                <div>
                    <label htmlFor='cr-name' className={labelClass}>
                        {t.name}
                    </label>
                    <input
                        id='cr-name'
                        type='text'
                        autoComplete='name'
                        required
                        value={values.name}
                        onChange={(e) => update('name', e.target.value)}
                        className={fieldClass}
                    />
                </div>
                <div>
                    <label htmlFor='cr-phone' className={labelClass}>
                        {t.phone}
                    </label>
                    <input
                        id='cr-phone'
                        dir='ltr'
                        type='tel'
                        autoComplete='tel'
                        required
                        value={values.phone}
                        onChange={(e) => update('phone', e.target.value)}
                        className={fieldClass}
                    />
                </div>
            </div>

            <div className='mt-5'>
                <label htmlFor='cr-email' className={labelClass}>
                    {t.email}
                </label>
                <input
                    id='cr-email'
                    dir='ltr'
                    type='email'
                    autoComplete='email'
                    required
                    value={values.email}
                    onChange={(e) => update('email', e.target.value)}
                    placeholder={messages.contactForm.emailPlaceholder}
                    className={fieldClass}
                />
            </div>

            <div className='mt-5'>
                <label htmlFor='cr-course' className={labelClass}>
                    {t.course}
                </label>
                <select
                    id='cr-course'
                    required
                    value={values.course}
                    onChange={(e) => update('course', e.target.value)}
                    className={cn(fieldClass, 'appearance-none bg-background')}
                >
                    {courses.map((course) => (
                        <option key={course.value} value={course.value}>
                            {course.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className='mt-5'>
                <label htmlFor='cr-message' className={labelClass}>
                    {t.message} <span className='font-normal text-muted-foreground'>{t.optional}</span>
                </label>
                <textarea
                    id='cr-message'
                    rows={4}
                    value={values.message}
                    onChange={(e) => update('message', e.target.value)}
                    placeholder={t.messagePlaceholder}
                    className={cn(fieldClass, 'resize-none')}
                />
            </div>

            <p className='mt-4 text-sm text-muted-foreground'>
                {t.notice}
            </p>

            {error && (
                <p className='mt-4 rounded-lg bg-destructive/10 px-4 py-2.5 text-sm text-destructive'>
                    {error}
                </p>
            )}

            <button
                type='submit'
                disabled={status === 'submitting'}
                className='group mt-6 inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-navy px-6 text-sm font-medium text-parchment transition-all duration-300 hover:-translate-y-0.5 hover:bg-navy-deep disabled:cursor-not-allowed disabled:opacity-70'
            >
                {status === 'submitting' ? (
                    <>
                        <Loader2 className='size-4 animate-spin' />
                        {t.submitting}
                    </>
                ) : (
                    <>
                        {t.submit}
                        <Send className='size-4 transition-transform duration-300 group-hover:translate-x-0.5 rtl:-scale-x-100' />
                    </>
                )}
            </button>
        </form>
    )
}
